/**
 * What this device remembers about its nests (`arrow:v1:nests`), and the gate.
 *
 * The api is the truth; this is a small, versioned cache of it: the nests this device is
 * in (id and name) and which one the Nest tab last showed. It exists so the app can
 * answer two questions without a network call:
 *
 * - **The gate.** Nests are hidden while we test (docs/NESTS.md, stage 2): the Nest tab,
 *   the poster's nest line and the privacy page's nest section show only when dev mode is
 *   on (src/dev/mode.ts, 12 taps on Nib in Stats) **or this device is in a nest**. The
 *   invite landing (`/n/:code`) and joining always work, so a real invitee can join with
 *   no dev mode, and from then on sees the Nest tab.
 * - **Solves.** A nest member's daily solves are sent even with "Count my plays" off
 *   (platform/sync.ts), because that's how the nest sees them.
 *
 * Refreshed from `GET /v1/nests` whenever the Nest tab opens, and written on create, join
 * and leave. A bad or old blob reads as "no nests" and never throws.
 */
import { useSyncExternalStore } from 'react';

import { useDevMode } from '../dev/mode.ts';
import { browserStore, type KeyValueStore } from '../storage/kv.ts';

export const NESTS_KEY = 'arrow:v1:nests';
const EVENT = 'arrow:nests';
/** The api's cap (docs/NESTS.md, "Rules"): a blob can't claim more. */
const MAX_NESTS = 10;

export interface KnownNest {
	id: string;
	name: string;
}

export interface NestMemory {
	v: 1;
	nests: KnownNest[];
	/** The nest the Nest tab shows (the last one picked), or null for the first. */
	current: string | null;
	/** The name you last gave a nest, to fill in next time. Only on this device. */
	name: string | null;
}

const EMPTY: NestMemory = { v: 1, nests: [], current: null, name: null };

/** Reads a stored blob defensively: anything unexpected is "no nests". */
export function parseNestMemory(raw: string | null): NestMemory {
	if (!raw) return EMPTY;
	try {
		const o = JSON.parse(raw) as Partial<NestMemory> | null;
		if (!o || o.v !== 1 || !Array.isArray(o.nests)) return EMPTY;
		const nests = o.nests
			.filter((n): n is KnownNest => !!n && typeof n.id === 'string' && n.id.length > 0 && n.id.length <= 64 && typeof n.name === 'string')
			.slice(0, MAX_NESTS)
			.map((n) => ({ id: n.id, name: n.name.slice(0, 80) }));
		const current = typeof o.current === 'string' && nests.some((n) => n.id === o.current) ? o.current : null;
		const name = typeof o.name === 'string' && o.name.trim() ? o.name.trim().slice(0, 40) : null;
		return { v: 1, nests, current, name };
	} catch {
		return EMPTY;
	}
}

let cache: { raw: string | null; value: NestMemory } | null = null;

export function nestMemory(store: KeyValueStore = browserStore()): NestMemory {
	const raw = store.get(NESTS_KEY);
	if (cache && cache.raw === raw) return cache.value;
	const value = parseNestMemory(raw);
	cache = { raw, value };
	return value;
}

function save(next: NestMemory, store: KeyValueStore): void {
	if (next.nests.length === 0) store.remove(NESTS_KEY);
	else store.set(NESTS_KEY, JSON.stringify(next));
	if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
}

/** Whether this device is in at least one nest (as far as it last heard). */
export function isNestMember(store: KeyValueStore = browserStore()): boolean {
	return nestMemory(store).nests.length > 0;
}

/**
 * Nests this device joined or started in this visit that the api's list may not have caught
 * up with yet. A list fetched just before (or while) the join landed doesn't have the new
 * nest, and replacing the memory with it dropped the nest and with it `current`, so the tab
 * fell back to the first nest (NJ-1). Until a list arrives that has it (or a couple of
 * minutes pass), it stays.
 */
const recent = new Map<string, number>();
const RECENT_MS = 120_000;

/** Replaces the list with what the api says (the Nest tab's refresh), keeping a nest just joined that it doesn't have yet. */
export function rememberNests(nests: readonly KnownNest[], store: KeyValueStore = browserStore()): void {
	const cur = nestMemory(store);
	const list = nests.map((n) => ({ id: n.id, name: n.name }));
	for (const n of list) recent.delete(n.id);
	const now = Date.now();
	for (const n of cur.nests) {
		const at = recent.get(n.id);
		if (at !== undefined && now - at < RECENT_MS && !list.some((x) => x.id === n.id)) list.push(n);
	}
	const kept = list.slice(-MAX_NESTS);
	const current = cur.current && kept.some((n) => n.id === cur.current) ? cur.current : null;
	if (JSON.stringify(kept) === JSON.stringify(cur.nests) && current === cur.current) return;
	save({ ...cur, nests: kept, current }, store);
}

/** Adds (or renames) one nest and makes it the current one: after create and join (`myName`: what you called yourself). */
export function rememberNest(nest: KnownNest, myName?: string, store: KeyValueStore = browserStore()): void {
	const cur = nestMemory(store);
	const nests = [...cur.nests.filter((n) => n.id !== nest.id), { id: nest.id, name: nest.name }].slice(-MAX_NESTS);
	recent.set(nest.id, Date.now());
	save({ v: 1, nests, current: nest.id, name: myName?.trim() || cur.name }, store);
}

export function forgetNest(id: string, store: KeyValueStore = browserStore()): void {
	const cur = nestMemory(store);
	recent.delete(id);
	save({ ...cur, nests: cur.nests.filter((n) => n.id !== id), current: cur.current === id ? null : cur.current }, store);
}

export function setCurrentNest(id: string, store: KeyValueStore = browserStore()): void {
	const cur = nestMemory(store);
	if (cur.current === id || !cur.nests.some((n) => n.id === id)) return;
	save({ ...cur, current: id }, store);
}

/** The nest to show: the current one, else the first. */
export function currentNest(memory: NestMemory): KnownNest | null {
	return memory.nests.find((n) => n.id === memory.current) ?? memory.nests[0] ?? null;
}

function subscribe(onChange: () => void): () => void {
	const onStorage = (e: StorageEvent) => {
		if (e.key === null || e.key === NESTS_KEY) onChange();
	};
	window.addEventListener(EVENT, onChange);
	window.addEventListener('storage', onStorage);
	return () => {
		window.removeEventListener(EVENT, onChange);
		window.removeEventListener('storage', onStorage);
	};
}

/** The device's nests, live. */
export function useNestMemory(): NestMemory {
	return useSyncExternalStore(subscribe, () => nestMemory(), () => EMPTY);
}

/** Nests are live for everyone (27 Oct 2026): the gate is always open. Kept as a rule so a
 * future switch has one place to go. */
export function nestsVisible(_devMode: boolean, _member: boolean): boolean {
	return true;
}

/** The gate, live: whether this device shows the Nest tab, the poster line and the privacy section. */
export function useNestsVisible(): boolean {
	const devMode = useDevMode();
	const memory = useNestMemory();
	return nestsVisible(devMode, memory.nests.length > 0);
}

/**
 * A nest just joined from an invite, for the page the invite lands on to confirm ("You're in
 * Family", QA2-13). The landing joins and moves on at once, so it leaves the nest's name
 * here, in memory, for the next page to take once. Gone after a few seconds, so a stale one
 * never turns up later.
 */
let joined: { name: string; at: number } | null = null;
const JOINED_MS = 10_000;

/** After joining from an invite. */
export function noteJoined(nestName: string, now = Date.now()): void {
	joined = { name: nestName, at: now };
}

/** The nest's name, once (null if there's none, or it's stale). */
export function takeJoined(now = Date.now()): string | null {
	const j = joined;
	joined = null;
	return j && now - j.at <= JOINED_MS ? j.name : null;
}
